<template>
  <div class="position-relative">
    <div
      ref="editor"
      v-if="!editable"
      class="display-only-text"
      :class="noWrapClass"
      v-html="text"
      :style="socialStyles"
    />

    <div
      v-else
      ref="editor"
      class="contenteditable-editor"
      :class="noWrapClass"
      contenteditable
      v-html="content"
      spellcheck="true"
      @input="onEditorChange"
      @paste="onEditorChange"
      @keydown="onKeyDown"
      @blur="onBlur"
      @focus="onFocus"
      @mouseup="onMouseUp"
    />

    <emoji-picker
      :search="search"
      @emoji="$emit('emoji', $event)"
    >
      <div
        class="emoji-invoker"
        slot="emoji-invoker"
        slot-scope="{ events: { click: clickEvent } }"
        @click.stop="clickEvent"
      >
        <span class="fas fa-smile" />
      </div>

      <div slot="emoji-picker" slot-scope="{ emojis, insert, display }">
        <div
          class="emoji-picker"
          :style="{
            bottom: `calc(${display.height - display.y}px - 0.5rem)`,
            right: `calc(100vw - ${display.x}px + 3rem)`,
          }"
        >
          <div class="emoji-picker__search">
            <input class="tb-input" type="text" v-model="search">
          </div>
          <div>
            <div v-for="(emojiGroup, category) in emojis" :key="category">
              <h5>{{ category }}</h5>
              <div class="emojis">
                <span
                  v-for="(emoji, emojiName) in emojiGroup"
                  :key="emojiName"
                  @click="insert(emoji)"
                  :title="emojiName"
                >{{ emoji }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </emoji-picker>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle, vue/no-mutating-props */

import { mapGetters } from 'vuex';

import EmojiPicker from './emoji-picker/EmojiPicker.vue';

/**
 * Reset cursor to start of element
 * @param {Selection} sel
 * @param {DOMElement} el
 */
const resetCursor = (sel, el) => {
  const range = document.createRange();
  range.setStart(el, 0);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

export default {
  components: {
    EmojiPicker,
  },

  props: {
    fontSize: {
      type: Number,
      default: null,
    },

    element: {
      type: Object,
      required: true,
    },

    editing: {
      type: Boolean,
      default: false,
    },

    editable: {
      type: Boolean,
      default: true,
    },

    text: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isEditing: false,
      content: this.text,

      previousFontSize: null,

      // Used as a hack to track updating state
      // and prevent infinite loops since we're reliant on global event listeners
      makingChanges: false,
      fineTuneIterations: 0,
      fineTuneMaxIterations: 1500,

      // For searching emojis
      search: '',
    };
  },

  computed: {
    ...mapGetters('graphicsTemplate', [
      'getCanvas',
      'isPropertyMapped',
    ]),

    ...mapGetters('panAndZoom', { scaleFactor: 'getScaleFactor' }),

    _fontSize: {
      get() {
        return this.fontSize;
      },
      set(v) {
        this.$emit('update:font-size', v);
      },
    },

    styles() {
      return this.element?.styles ?? {};
    },

    needsNoWrap() {
      return this.styles.textFlowOption === 2;
    },

    noWrapClass() {
      return this.needsNoWrap ? 'editor-no-wrap' : '';
    },

    characterCount() {
      const div = document.createElement('div');
      div.innerHTML = this.element.text;
      return div.textContent.length;
    },

    editorEl() {
      return this.$refs.editor;
    },

    socialStyles() {
      if (this.element.network === 'reddit') {
        return 'pointer-events: none;';
      }

      return 'white-space: pre-wrap; pointer-events: none;';
    },
  },
  watch: {
    editing(isEditing) {
      if (isEditing) {
        this.editorEl.focus();
      } else {
        this.editorEl.blur();
      }
    },

    text(val) {
      if (val !== this.content && !this.isEditing) {
        this.content = val;
        this.editorEl.innerHTML = val;
      }
    },

    'element.fitText': {
      handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return;
        }

        // store/restore previous font size when entering/leaving autoscale
        if (newVal) {
          this.previousFontSize = this._fontSize;
        } else if (oldVal) {
          this._fontSize = this.previousFontSize;
        }
      },
    },
  },

  created() {
    // modernize previously created text elements
    // we have other elements using this (like social-text) so limit scope
    if (this.element.type === 'text' && this.element.version === 'v1') {
      this.$set(this.element.styles, 'textFlowOption', 2);
      this.$set(this.element.styles, 'textBoxOption', 1);
      if (this.element.fitText) {
        this.$set(this.element.styles, 'maxSizeText', this.fontSize);
      }

      this.element.version = 'v2';
    }

    this.previousFontSize = this.fontSize;
  },

  mounted() {
    if (this.$refs.editor) {
      this.$refs.editor.addEventListener('paste', this.onPaste);
    }
  },

  beforeUnmount() {
    if (this.$refs.editor) {
      this.$refs.editor.removeEventListener('paste', this.onPaste);
    }
  },

  methods: {
    onPaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertHTML', false, text);
    },

    isMapped(property) {
      return this.isPropertyMapped(this.element.id, property);
    },

    onMouseUp(e) {
      if (this.isEditing) {
        e.stopPropagation();
      }
    },

    onBlur(e) {
      this.isEditing = false;
      this.onEditorChange(e);
      setTimeout(() => {
        e.target.innerHTML = this.text;
        this.content = this.text;
      });
    },

    onFocus() {
      this.isEditing = true;
    },

    onKeyDown(e) {
      if (e.key === 'Backspace') {
        // Check if cursor is at the beginning of a <li>
        const sel = window.getSelection();
        const node = sel.anchorNode || sel.focusNode;
        const parentNode = node?.parentNode;

        if (
          sel.anchorOffset === 0 &&
          ['LI', 'OL', 'UL'].includes(parentNode?.nodeName)
        ) {
          e.preventDefault();
          e.stopPropagation();

          let listEl;
          let listItemEl;

          if (parentNode.nodeName === 'LI') {
            listItemEl = parentNode;
            listEl = listItemEl.parentNode;
          } else {
            listEl = parentNode;
            listItemEl = listEl.firstChild;
          }

          const textEl = document.createTextNode(listItemEl.textContent);

          if (listItemEl.previousSibling && listItemEl.nextSibling) {
            // Split list in half
            // NodeLists are dumb btw
            const { children } = listEl;
            const idx = Array.prototype.indexOf.call(children, listItemEl);

            const ch1 = Array.prototype.slice.call(children, 0, idx);
            const ch2 = Array.prototype.slice.call(children, idx + 1);

            const list1 = document.createElement(listEl.nodeName);
            const list2 = document.createElement(listEl.nodeName);

            ch1.forEach((ch) => {
              list1.appendChild(ch);
            });

            ch2.forEach((ch) => {
              list2.appendChild(ch);
            });

            listEl.replaceWith(list1);
            list1.after(textEl);
            textEl.after(list2);
          } else if (listItemEl.previousSibling) {
            // Remove <li> and add content after list
            listEl.removeChild(listItemEl);
            listEl.after(textEl);
          } else if (listItemEl.nextSibling) {
            // Remove <li> and add content before list
            listEl.removeChild(listItemEl);
            listEl.before(textEl);
          } else {
            // Delete the whole list
            listEl.replaceWith(textEl);
          }

          // Set caret at beginning of line
          resetCursor(sel, textEl);
        }
      }
    },

    onEditorChange(e) {
      const text = e.target.innerText;
      let html = e.target.innerHTML;

      // Check for special characters to replace (e.g. "- " turns into an <ul>)
      if (e.inputType === 'insertText' && e.data === ' ') {
        const trimLeft = (str) => str.replace(/^\s+/, '');
        const sel = window.getSelection();
        const node = sel.anchorNode || sel.focusNode;
        const data = trimLeft(node.textContent || '');

        // "Smart" list creation when a `-` or number is input.
        // if (/^-\s/.test(data) || /^\d+\.\s/.test(data)) {
        if (/^-\s/.test(data)) {
          // Replace current line with list element
          const listType = data.indexOf('-') === 0 ? 'ul' : 'ol';
          const contents = data.slice(2);
          const parent = node.parentNode;
          const listEl = document.createElement(listType);
          const li = document.createElement('li');
          li.innerText = trimLeft(contents);
          listEl.appendChild(li);
          parent.insertBefore(listEl, node);
          parent.removeChild(node);

          // Set caret at beginning of list item
          resetCursor(sel, li);
        }
      }

      // Merge list nodes that are next to each other
      e.target.childNodes.forEach((node) => {
        const child = node;
        const sibling = child.nextSibling;

        if (!child.querySelector) {
          return;
        }

        const listEl = ['UL', 'OL'].includes(child.nodeName)
          ? child
          : child.querySelector('ul') || child.querySelector('ol');

        if (listEl && sibling) {
          const listType = listEl.nodeName.toLowerCase();
          const siblingListEl = ['UL', 'OL'].includes(sibling.nodeName)
            ? sibling
            : sibling?.querySelector &&
              (sibling?.querySelector('ul') || sibling?.querySelector('ol'));

          if (
            siblingListEl &&
            listType === siblingListEl.nodeName.toLowerCase()
          ) {
            // Merge!
            listEl.innerHTML += siblingListEl.innerHTML;
            siblingListEl.parentNode.removeChild(siblingListEl);
          }
        }
      });

      const { textContent } = e.target;
      const lineBreakCount = (text.match(/\n/g) || []).length;
      const len = textContent.length + lineBreakCount;

      // enforce character length
      if (
        this.element.limitCharacters &&
        len > this.element.maxCharacterLength
      ) {
        // Clone contenteditable so contents aren't edited in place
        const clone = e.target.cloneNode(true);
        const nodes = [];
        clone.childNodes.forEach((child) => {
          nodes.unshift(child);
        });

        const textCount = nodes.reduce(
          (accum, node) => accum + node.textContent.length,
          0,
        );

        let overflow = textCount - this.element.maxCharacterLength;

        for (let i = 0; i < nodes.length; i += 1) {
          const node = nodes[i];

          const t = node.textContent;

          if (t.length >= overflow) {
            // Slice up inner text and break out
            node.textContent = t.substr(0, t.length - overflow);
            break;
          }

          // Clear text of node, and continue to the next one
          overflow -= t.length;
          node.textContent = '';
        }

        html = clone.innerHTML;
      }

      // Sanitize HTML to remove browser-added spans with inline styles
      const sanitized = this.sanitizeHtml(html);
      this.$emit('update:text', sanitized);
    },

    sanitizeHtml(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('span[style]').forEach((span) => {
        span.replaceWith(...span.childNodes);
      });

      return div.innerHTML;
    },

    getTextWidth() {
      return [...this.editorEl.childNodes].reduce(
        (acc, child) => {
          if (child.offsetWidth > acc.width) {
            acc.width = child.offsetWidth;
            acc.length = child.textContent.length;
          }

          return acc;
        },
        { width: 0, length: 0 },
      );
    },

    getTextHeight() {
      return [...this.editorEl.childNodes].reduce(
        (acc, child) => acc + child.offsetHeight,
        0,
      );
    },

    scaleText() {
      if (!this.editorEl.textContent || (!this.element.text && !this.isMapped('text'))) {
        return;
      }

      this.fineTuneIterations = 0;

      // adapted from here: https://github.com/BrOrlandi/big-text.js/blob/master/big-text.js
      const { width, height } = this.styles;
      const fontSizeFactor = 0.8;

      // set styles to get sizes of just text
      this.editorEl.style.fontSize = `${1000 * fontSizeFactor}px`;
      const children = this.editorEl.childNodes;

      children.forEach((el) => {
        const child = el;

        if (child.nodeName === '#text') {
          if (!child.textContent.trim()) {
            this.editorEl.removeChild(child);
            return;
          }

          const element = document.createElement('div');
          this.editorEl.insertBefore(element, child);
          element.innerText = child.textContent;
          this.editorEl.removeChild(child);
          element.style.alignSelf = 'flex-start';
          return;
        }

        child.style.alignSelf = 'flex-start';
      });

      // Strip out extra <br>
      const lastChild = children[children.length - 1];
      if (lastChild?.nodeName === 'BR') {
        this.editorEl.removeChild(lastChild);
      }

      // find widest child
      const childData = this.getTextWidth();

      // total children height
      const { letterSpacing } = this.styles;
      const childrenHeight = this.getTextHeight();
      const widthFactor =
        (width - childData.length * letterSpacing) / childData.width;
      const heightFactor = height / childrenHeight;

      let lineHeight;

      if (this.styles.textFlowOption === 2) {
        // DO things differently because of wrapping
        // TODO: this could be a more accurate guess
        const hyp1 = Math.sqrt(width ** 2 + height ** 2);
        const hyp2 = Math.sqrt(childData.width ** 2 + childrenHeight ** 2);

        const hypFactor = hyp1 / hyp2;

        lineHeight = Math.floor(hypFactor * 1000);
      } else if (heightFactor > widthFactor) {
        lineHeight = Math.floor(widthFactor * 1000);
      } else {
        lineHeight = Math.floor(heightFactor * 1000);
      }

      const fontSize = Math.floor(lineHeight * fontSizeFactor);

      this._fontSize = fontSize;

      this.editorEl.style.removeProperty('font-size');

      const { maxSizeText } = this.styles;
      // Sometimes the size is slightly off but close so this fine tunes it
      const fineTune = (lastCheck = false) => {
        this.$nextTick(() => {
          // Prevent infinitely small behavior
          if (this._fontSize <= 1 || width <= 1 || height <= 1) {
            this._fontSize = 1;
            this.makingChanges = false;
            return;
          }

          // Prevent infinite loops
          this.fineTuneIterations += 1;
          if (this.fineTuneIterations >= this.fineTuneMaxIterations) {
            children.forEach((child) => {
              child.style.removeProperty('align-self');
            });

            this.makingChanges = false;
            return;
          }

          const textWidth = this.getTextWidth().width;
          const textHeight = this.getTextHeight();

          const tooWide = textWidth > width;
          const tooTall = textHeight > height;

          if (lastCheck && (tooWide || tooTall)) {
            // Bigger didn't fit, go back down and be done
            this._fontSize -= 1;

            children.forEach((child) => {
              child.style.removeProperty('align-self');
            });

            this.makingChanges = false;
          } else if (tooWide || tooTall) {
            // Too big, scale down
            let sizeDiff = 1;

            // this tends to be off
            if (this.styles.textFlowOption === 2) {
              let ratio;
              if (tooWide) {
                ratio = textWidth / width;
              } else if (tooTall) {
                ratio = textHeight / height;
              }

              // Pi is a magic number that somehow works "best" :P
              sizeDiff = Math.max(1, Math.floor(ratio * Math.PI));
            }

            this._fontSize -= sizeDiff;
            fineTune();
          } else {
            // it fits, enforce max size (if set) and be done
            if (maxSizeText > 0 && this._fontSize > maxSizeText) {
              this._fontSize = Math.min(this._fontSize, maxSizeText);
              children.forEach((child) => {
                child.style.removeProperty('align-self');
              });

              this.makingChanges = false;
              return;
            }

            // else see if bigger fits
            this._fontSize += 1;
            fineTune(true);
          }
        });
      };

      this.makingChanges = true;
      fineTune();
    },
    scaleContainer() {
      this.editorEl.childNodes.forEach((el) => {
        const child = el;

        if (child.nodeName === '#text') {
          if (!child.textContent.trim()) {
            return;
          }

          const element = document.createElement('div');
          this.editorEl.insertBefore(element, child);
          element.appendChild(child);
          element.style.alignSelf = 'flex-start';
          return;
        }

        child.style.alignSelf = 'flex-start';
      });

      // Resize container if not wrapping, otherwise preserve width and only scale height
      const width = (
        this.styles.textFlowOption === 1
          ? this.getTextWidth().width
          : this.styles.width
      );

      const height = this.getTextHeight();

      // check if overflowing right side and move it over
      if (this.styles.left + width > this.getCanvas.width) {
        this.element.styles.left = this.getCanvas.width - width;
      }

      // set styles
      this.element.styles.width = width;
      this.element.styles.height = height;

      // disable listener to avoid infinite loop, emit, then reenable
      this.makingChanges = true;
      this.$nextTick(() => {
        this.editorEl.childNodes.forEach((child) => {
          if (child.nodeName === '#text') {
            if (!child.textContent.trim()) {
              return;
            }

            const element = document.createElement('div');
            this.editorEl.insertBefore(element, child);
            element.appendChild(child);
            element.style.alignSelf = 'flex-start';
            return;
          }

          child.style.removeProperty('align-self');
        });

        this.$nextTick(() => {
          this.makingChanges = false;
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.contenteditable-editor {
  min-height: 6.25rem;
  max-height: 12.5rem;
  overflow-y: auto;
  display: block;
  width: 100%;
  padding: 0.375rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.4;
  color: #E1E1E1;
  background: #333333;
  border-radius: 2px;
  border: 1px solid transparent;
  outline: 0;
  box-shadow: none;
  -webkit-appearance: none;
}
</style>
